import Heart from "@/components/svg/Heart";
import { isFilled } from "@prismicio/client";
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next";
import { PrismicRichText } from "@prismicio/react";

/**
 * @param {import("@prismicio/client").Content.LogosSliceDefaultPrimaryLogosItem} item
 */
const LogoItem = ({ item }) => {
  if (!isFilled.image(item.logo)) {
    return null;
  }

  const image = (
    <PrismicNextImage
      field={item.logo}
      className="h-[50px] w-auto object-contain"
      fallbackAlt=""
    />
  );

  if (isFilled.contentRelationship(item.cta)) {
    return (
      <PrismicNextLink prefetch={false}
        field={item.cta}
        className="inline-flex items-center no-underline opacity-90 transition-opacity hover:opacity-100"
      >
        {image}
      </PrismicNextLink>
    );
  }

  return <div className="inline-flex items-center">{image}</div>;
};

/**
 * @typedef {import("@prismicio/client").Content.LogosSlice} LogosSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<LogosSlice>} LogosProps
 * @type {import("react").FC<LogosProps>}
 */
const Logos = ({ slice }) => {
  const logos = Array.isArray(slice.primary.logos) ? slice.primary.logos : [];

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="contre-uno bg-beige-30 py-2.5 padding-x-md"
    >
      <div className="framing-lg flex flex-col items-start gap-8 md:gap-4 lg:flex-row lg:items-center lg:justify-center lg:gap-30">
        {isFilled.richText(slice.primary.title) && (
          <div className="hidden items-center gap-3 lg:flex">
            <Heart />
            <PrismicRichText
              field={slice.primary.title}
              components={{
                paragraph: ({ children }) => (
                  <p className="section-text-n text-bleu">{children}</p>
                ),
                heading2: ({ children }) => (
                  <p className="section-text-n text-bleu">{children}</p>
                ),
                heading3: ({ children }) => (
                  <p className="section-text-n text-bleu">{children}</p>
                ),
              }}
            />
          </div>
        )}

        {logos.length > 0 && (
          <div className="w-full min-w-0 lg:flex-1">
            <div className="flex px-5 snap-x snap-mandatory items-center gap-10 overflow-x-auto scroll-smooth pb-1 [-ms-overflow-style:none] [scrollbar-width:none] lg:hidden [&::-webkit-scrollbar]:hidden">
              {logos.map((item, index) => (
                <div
                  key={`mobile-logo-${index}`}
                  className="flex shrink-0 snap-center items-center"
                >
                  <LogoItem item={item} />
                </div>
              ))}
            </div>

            <div className="hidden w-full items-center justify-between gap-6 lg:flex">
              {logos.map((item, index) => (
                <LogoItem key={`desktop-logo-${index}`} item={item} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Logos;
